import {
  makeStyles,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography
} from '@paintscout/ui'
import { EventsMap, useDialogs, useClientOptions } from '@paintscout/ui-business'
import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'
import type { User } from '@paintscout/api'
import { escapeHref, getEventLabel, getTimezone, groupMonthViewEventsByEstimator } from '@paintscout/util'
import { translateEventType, uuid } from '@paintscout/util/builder'
import type { DialogProps } from '@paintscout/ui'
import { useCancelEventFlow } from 'src/hooks'
import moment from 'moment-timezone'
import type { CalendarEvent } from 'paintscout'
import React, { useState } from 'react'
import EventDialog from '../EventDialog'
import { EventCard } from './EventCard'

const useStyles = makeStyles((theme) => ({
  closeButton: {
    color: theme.palette.text.primary
  },
  dialogContent: {
    paddingTop: theme.spacing(0.5)
  },
  content: {
    marginTop: theme.spacing(1)
  }
}))

export interface ViewEventsDialogProps extends DialogProps {
  events: CalendarEvent[]
  date?: Date
  updateEvent: (event: CalendarEvent, eventTemplate: string) => void
  deleteEvent: (event: CalendarEvent, eventTemplate: string) => void
  addEvent: (event: CalendarEvent, eventTemplate: string) => void
  groupByEstimator?: boolean
  selectedUsers?: User[]
}

export default function ViewEventsDialog(props: ViewEventsDialogProps) {
  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()
  const { events, updateEvent, deleteEvent, addEvent, groupByEstimator, selectedUsers = [], ...dialogProps } = props
  const classes = useStyles()
  const { options } = useClientOptions()
  const timezone = getTimezone({ options })

  const [tab, setTab] = useState('list')

  // `props.date` is a browser-local Date from a calendar slot click — format
  // it bare so it matches the cell the user clicked. The fallback is an event
  // start (UTC-stored), which needs `.tz(timezone)` to render in company tz.
  const date = props.date
    ? moment(props.date).format('MMMM D, YYYY')
    : moment(events[0].start).tz(timezone).format('MMMM D, YYYY')
  const estimatorName = events[0].estimator?.name

  const sortEvents = (events: CalendarEvent[]) => {
    return [...events].sort((a, b) => {
      const startA = new Date(a.start).getTime()
      const startB = new Date(b.start).getTime()
      return startA - startB
    })
  }

  const sortedEvents = sortEvents(groupByEstimator ? groupMonthViewEventsByEstimator(events) : events)

  const handleEditEvent = (event: CalendarEvent) => {
    if (event.eventType === '_google-calendar_') {
      return
    }
    openDialog(EventDialog, {
      event,
      onDelete: deleteEvent,
      onConfirm: (updatedEvent: CalendarEvent, eventTemplate: string) => {
        updateEvent(updatedEvent, eventTemplate)
        dismissAllDialogs()
      }
    })
  }

  const handleAddEvent = () => {
    const lastNonAllDayEvent = [...sortedEvents].reverse().find((e) => !e.allDay)
    const baseTime = lastNonAllDayEvent
      ? lastNonAllDayEvent.end
      : moment(date)
          .set({
            hour: moment().clone().add(1, 'hour').startOf('hour').hour(),
            minute: 0,
            second: 0,
            millisecond: 0
          })
          .toDate()

    openDialog(EventDialog, {
      isNew: true,
      event: {
        id: uuid()
      },
      date: baseTime,
      selectedUsers: selectedUsers?.length ? selectedUsers : undefined,
      onConfirm: (newEvent: CalendarEvent, eventTemplate: string) => {
        addEvent(newEvent, eventTemplate)
        dismissAllDialogs()
      },
      onCancel: dismissDialog
    })
  }

  const cancelEventFlow = useCancelEventFlow()
  const handleCancelEvent = (event: CalendarEvent) => cancelEventFlow(event, deleteEvent)

  const getUngroupedEvents = () => {
    const ungroupedEvents: CalendarEvent[] = []
    for (const event of sortedEvents) {
      ungroupedEvents.push(event)
      ungroupedEvents.push(...(event.subsequentEvents || []))
    }
    return ungroupedEvents
  }

  const EventList = ({ events }: { events: CalendarEvent[] }) => {
    const items = events.map((event) => {
      const contactName = event.contact ? `${event.contact.firstName || ''} ${event.contact.lastName || ''}`.trim() : ''
      const address = event.address && !event.address.includes('undefined') ? event.address : ''
      const comma = contactName && address ? ', ' : ''
      const isIntegrationEvent = event.eventType === '_google-calendar_'
      const start = moment(event.start).tz(timezone)
      const end = event.end ? moment(event.end).tz(timezone) : start

      const timeLabel = getEventLabel(start, end, true)
      const transparency = event.transparency === 'transparent' ? 'Free' : 'Busy'
      const title = event.title || 'Event'
      const attendees = event.attendees || 0

      const sublabelParts = []
      if (isIntegrationEvent) {
        if (attendees > 0) {
          sublabelParts.push(`${attendees} Guest${attendees > 1 ? 's' : ''}`)
        }
        if (address) {
          sublabelParts.push(address)
        }
      }

      return {
        key: event.id,
        label: `${timeLabel} ${isIntegrationEvent ? title : translateEventType({ eventType: event.eventType, options })}`,
        sublabel: isIntegrationEvent
          ? `Imported from Google Calendar (${transparency})${sublabelParts?.length ? '\n' + sublabelParts.join(', ') : ''}`
          : `${contactName}${comma}${address}`,
        dashed: isIntegrationEvent,
        borderColor: event.estimator?.color || null,
        address: address || null,
        description: isIntegrationEvent ? event.note : undefined,
        isIntegrationEvent,
        isPast: end.isBefore(moment())
      }
    })

    const handleActionClick = (_: React.MouseEvent, action: string, key: string) => {
      const event = events.find((e) => e.id === key)
      switch (action) {
        case 'view-deal': {
          const dealId = event.deal?.id
          if (dealId) {
            window.location.href = escapeHref(`/deals/edit/${encodeURIComponent(dealId)}`)
          }
          break
        }
        case 'open-maps': {
          if (event.address) {
            window.open(
              `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address)}`,
              '_blank'
            )
          }
          break
        }

        case 'edit':
          handleEditEvent(events.find((e) => e.id === key))
          break
        case 'cancel':
          handleCancelEvent(events.find((e) => e.id === key))
          break
      }
    }

    return (
      <div style={{ marginBottom: 16 }}>
        {items.map((item) => (
          <EventCard
            key={item.key}
            item={item}
            onClick={() => handleEditEvent(events.find((e) => e.id === item.key))}
            onActionClick={(actionKey) => handleActionClick(undefined as any, actionKey, item.key)}
          />
        ))}
      </div>
    )
  }

  return (
    <Dialog open {...dialogProps}>
      <DialogTitle
        rightContent={
          <IconButton className={classes.closeButton} size="small" onClick={dismissDialog}>
            <CloseIcon />
          </IconButton>
        }
      >
        {date} {!groupByEstimator && `- ${estimatorName}`}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div>
          <Tabs
            value={tab}
            onChange={(_, value) => {
              setTab(value)
            }}
          >
            <Tab label="List" value="list" onClick={() => setTab('list')} />
            <Tab label="Map" value="map" onClick={() => setTab('map')} />
          </Tabs>
        </div>
        <div className={classes.content}>
          {tab === 'list' && (
            <>
              {groupByEstimator ? (
                <>
                  {sortedEvents.map((event, index) => {
                    const estimatorName = event.estimator?.name || 'Unknown Estimator'
                    return (
                      <div key={index}>
                        <Typography weight={500} gutterBottom>
                          {estimatorName}
                        </Typography>
                        <EventList key={event.id} events={sortEvents([event, ...event.subsequentEvents])} />
                      </div>
                    )
                  })}
                </>
              ) : (
                <EventList events={sortedEvents} />
              )}
            </>
          )}
          {tab === 'map' && (
            <EventsMap events={sortEvents(getUngroupedEvents())} onMarkerClick={handleEditEvent} dayView />
          )}
        </div>
      </DialogContent>
      {!moment(date).isBefore(moment(), 'day') && (
        <DialogActions>
          <Button icon={AddIcon} onClick={handleAddEvent}>
            Add Event
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
